/**
 * insertSectionIntoGrid — add a section to a page as a first-class block.
 *
 * A section is a full-width top-level GridItem in the page grid
 * (`contentType:"grid"`, `w:12`, `nestedGridId`); its widgets live in a
 * separate NESTED grid (`grid:<sectionGridId>`). The page grid becomes a
 * vertical stack of section blocks (order = y), so dragging a block
 * vertically reorders sections (grid compaction).
 *
 * Shared by the add-section paths so they can't drift:
 *   • AddDragProvider (drag a section tile → drop onto a page)
 *   • PageNode seeding (insert default sections into a new page)
 *
 * This is a PLAIN helper — it does NOT bind the nested grid to Yjs.
 * PageNode owns nested-grid bindings via a reconciliation effect: after
 * this seeds the nested grid + adds the block, PageNode detects the new
 * section item and binds `grid:<sectionGridId>` (the late-joiner binding
 * pushes the seeded items store→Yjs, persisting them).
 *
 * No-op for an unknown templateId or a page grid not in the store yet.
 */
import {
  useGridStore,
  generateId,
  gapOf,
  gridToPixel,
} from "@fredericrous/grid";
import type { GridState } from "@fredericrous/grid";

import { SECTION_TEMPLATES } from "./sectionTemplates.js";
import {
  SECTION_NESTED_CONFIG,
  sectionBlockRows,
} from "@website-builder/site-authoring";

/**
 * Section geometry lives in `@website-builder/site-authoring` (shared with the
 * headless authoring API so a template authored there sizes exactly as the
 * editor does). Re-exported here — the editor's imports are unchanged.
 */
export {
  SECTION_GRID_CONFIG,
  SECTION_NESTED_CONFIG,
  SECTION_CHROME_PX,
  sectionBlockRows,
} from "@website-builder/site-authoring";
import { EDITOR_ROW_HEIGHT_PX } from "@website-builder/schemas";

/** Bottom-most nested row a template's widgets occupy (max of y+h). */
function templateContentRows(
  items: ReadonlyArray<{ position: { y: number; h: number } }>,
): number {
  let bottom = 0;
  for (const it of items) {
    const b = it.position.y + it.position.h;
    if (b > bottom) bottom = b;
  }
  return bottom;
}

export function insertSectionIntoGrid(
  pageGridId: string,
  templateId: string,
  /** Ordinal among the page's section blocks (0..n) to insert at; sections
   *  at/after it shift down. Omit (or pass an out-of-range index) to append. */
  insertIndex?: number,
  /** DETERMINISTIC id base for the DEFAULT-site SEED path only. When set, the
   *  section grid, its block, and its widgets get stable ids derived from it
   *  (not `generateId`), so two contexts seeding the same fresh page concurrently
   *  (leader + late-synced follower — see useEditorDoc) write the SAME keys and
   *  CRDT-converge to ONE section instead of duplicating. The interactive
   *  "drag a section onto a page" path omits it → fresh random ids per instance. */
  idBase?: string,
): string | undefined {
  const tpl = SECTION_TEMPLATES.find((t) => t.id === templateId);
  if (!tpl) return undefined;

  const store = useGridStore.getState();

  // Guard FIRST: the page grid must already exist, else addItem (below)
  // no-ops and we'd leave the seeded nested grid orphaned in the store.
  const pageGrid = store.grids[pageGridId];
  if (!pageGrid) return undefined;

  // 1. Create + seed the section's nested grid with the template widgets.
  //    Under idBase, ids are deterministic (idempotent seed); else random.
  const sectionGridId = idBase ? `${idBase}-grid` : generateId("section");
  const tplItems = tpl.items();
  // Derive the block height from the SEEDED content, px-true, instead of the
  // template's advisory `height` — which undercounted the nested padding+gaps
  // and left the last widget overflowing the section (Hero subtext bug).
  const blockH = sectionBlockRows(templateContentRows(tplItems));
  store.importLayout(sectionGridId, {
    config: SECTION_NESTED_CONFIG,
    items: idBase
      ? tplItems.map((it, i) => ({ ...it, id: `${idBase}-w${i}` }))
      : tplItems,
  });

  // 2. Choose the block's target y. Default: append below all sections.
  //    With an in-range insertIndex, place it at that ordinal and shift the
  //    sections at/after it down — SHIFT BEFORE ADD so compaction's (y,x) tie
  //    can't reorder the new block past the moved ones.
  const sections = Object.entries(pageGrid.items)
    .filter(([, it]) => it.contentType === "grid")
    .sort((a, b) => a[1].position.y - b[1].position.y);

  let maxY = 0;
  for (const item of Object.values(pageGrid.items)) {
    const bottom = item.position.y + item.position.h;
    if (bottom > maxY) maxY = bottom;
  }

  let targetY = maxY;
  if (insertIndex != null && insertIndex < sections.length) {
    targetY = sections[insertIndex][1].position.y;
    for (let i = insertIndex; i < sections.length; i++) {
      const [sid, sit] = sections[i];
      store.updateItem(pageGridId, sid, {
        position: { ...sit.position, y: sit.position.y + blockH },
      });
    }
  }

  const secItemId = idBase ? `${idBase}-block` : generateId("secitem");
  store.addItem(pageGridId, {
    id: secItemId,
    position: { x: 0, y: targetY, w: 12, h: blockH },
    // minW/maxW=12 pins the block full-width (clampPosition forces x=0);
    // noResize keeps it a clean stacked block (reorder is vertical-only).
    constraints: { minW: 12, maxW: 12, noResize: true },
    contentType: "grid",
    // Set on BOTH the field (serializeSiteDoc reads item.nestedGridId) and
    // contentProps (GridWidget reads contentProps.nestedGridId).
    nestedGridId: sectionGridId,
    contentProps: {
      ...tpl.sectionProps?.(),
      nestedGridId: sectionGridId,
      label: tpl.label,
    },
  });
  return secItemId;
}

/**
 * Prepare a section for the grid engine's EXTERNAL-ADD drag (palette → page
 * body): seed the nested grid with the template's widgets NOW and return the
 * ExternalAddSpec that will commit the section block at the previewed landing —
 * the same drag machinery a cross-grid section MOVE uses, in place of the old
 * bespoke body-only insert. The nested grid is created up front so the block's
 * `nestedGridId` is populated during the drag (the engine wires its parent
 * back-reference on commit; PageNode's reconciliation effect then binds it to
 * Yjs, exactly as {@link insertSectionIntoGrid}).
 *
 * The caller MUST either commit the drag (endExternalAdd) or, on cancel/drop-
 * outside, delete the seeded nested grid (`deleteGrid(sectionGridId)`) so a
 * cancelled drag leaves no orphan. Null for an unknown templateId.
 */
export function prepareSectionExternalAdd(templateId: string) {
  const tpl = SECTION_TEMPLATES.find((t) => t.id === templateId);
  if (!tpl) return null;
  const store = useGridStore.getState();
  const sectionGridId = generateId("section");
  const tplItems = tpl.items();
  const blockH = sectionBlockRows(templateContentRows(tplItems));
  store.importLayout(sectionGridId, {
    config: SECTION_NESTED_CONFIG,
    items: tplItems,
  });
  return {
    sectionGridId,
    spec: {
      contentType: "grid",
      w: 12,
      h: blockH,
      nestedGridId: sectionGridId,
      // Set on BOTH (serializeSiteDoc reads item.nestedGridId; GridWidget reads
      // contentProps.nestedGridId) — mirrors insertSectionIntoGrid's addItem.
      contentProps: {
        ...tpl.sectionProps?.(),
        nestedGridId: sectionGridId,
        label: tpl.label,
      },
      // Pin full-width, non-resizable — a clean stacked block (vertical reorder
      // only), same constraints insertSectionIntoGrid gives the block.
      constraints: { minW: 12, maxW: 12, noResize: true },
    },
  };
}

/** Default width of a new Inline, in the section grid's columns. */
export const INLINE_DEFAULT_COLUMNS = 6;

/** Config of an Inline's nested grid: ONE row of `columns` cells (the
 *  Inline's width), packed left, at the section's row height. */
export function inlineNestedConfig(columns: number) {
  return {
    ...SECTION_NESTED_CONFIG,
    columns,
    compactType: "horizontal" as const,
  };
}

/**
 * Prepare an Inline for the grid engine's EXTERNAL-ADD drag, the way
 * {@link prepareSectionExternalAdd} does a section: create its (empty) nested
 * grid now and return the spec that commits the Inline block at the previewed
 * landing. The caller MUST commit the drag or delete `nestedGridId` on
 * cancel, so no orphan grid is left behind. Born empty: no preset children.
 */
export function prepareInlineExternalAdd(columns = INLINE_DEFAULT_COLUMNS) {
  const store = useGridStore.getState();
  const nestedGridId = generateId("inline");
  store.importLayout(nestedGridId, {
    config: inlineNestedConfig(columns),
    items: [],
  });
  return {
    nestedGridId,
    spec: {
      contentType: "inline-grid",
      w: columns,
      h: 1,
      // On BOTH, as for a section: serializeSiteDoc reads item.nestedGridId,
      // the widget reads contentProps.nestedGridId.
      nestedGridId,
      contentProps: { nestedGridId, justify: "start" },
    },
  };
}

/** Escape a string for use inside a CSS attribute selector. `CSS.escape` is
 *  absent in jsdom (unit tests) — fall back to escaping the two chars that
 *  break a double-quoted attribute value. */
function escapeAttr(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/["\\]/g, "\\$&");
}

export interface SectionDropTarget {
  /** Insertion ordinal among the page's section blocks (0..n). */
  readonly index: number;
  /** Marker Y in page-grid-container-local pixels. */
  readonly markerTop: number;
}

/** A section block's vertical extent in grid-container-local pixels. */
export interface SectionSpan {
  readonly top: number;
  readonly height: number;
}

/**
 * Pure: the insertion ordinal for a pointer at `localY` over vertically-stacked
 * sections `spans` (in visual order). The first section whose midpoint is below
 * the pointer is the slot; past the last midpoint ⇒ append (`spans.length`).
 */
export function pickSectionInsertIndex(
  spans: readonly SectionSpan[],
  localY: number,
): number {
  for (let i = 0; i < spans.length; i++) {
    if (localY < spans[i]!.top + spans[i]!.height / 2) return i;
  }
  return spans.length;
}

/**
 * Pure: the marker Y (grid-container-local px) for insertion at `index` — just
 * above the section there, or below the last one when appending; `padding` when
 * the page has no sections yet.
 */
export function sectionMarkerTop(
  spans: readonly SectionSpan[],
  index: number,
  gap: number,
  padding: number,
): number {
  if (spans.length === 0) return padding;
  if (index < spans.length) return spans[index]!.top - gap / 2;
  const last = spans[spans.length - 1]!;
  return last.top + last.height + gap / 2;
}

/**
 * PURE: resolve where a section dropped at `pointerClientY` lands in `grid`,
 * given the grid container's `originY` (its top edge in the SAME coordinate
 * space as `pointerClientY` — client px on web, screen px on native). No DOM,
 * no measurement: callers supply the measured origin via a platform adapter
 * (web `getBoundingClientRect`, native `measureInWindow`). This is the
 * cross-target core; `computeSectionDropTarget` is the web adapter around it.
 *
 * Returns null when the grid isn't measured yet (containerWidth 0).
 */
export function computeSectionDropTargetFromGeometry(
  grid: GridState,
  originY: number,
  pointerClientY: number,
  /** Container render scale (xyflow zoom): the origin + pointer are in scaled
   *  SCREEN px, but the section spans are UNSCALED grid-local px, so the screen
   *  offset must be divided back by the scale. 1 when unscaled (e.g. native). */
  scale = 1,
  /** Measured content width to use instead of the store's `containerWidth`.
   *  At far zoom the real grid isn't mounted, so its stored width can be stale
   *  or 0 — the caller measures the page-body element and passes it here. */
  widthOverride?: number,
): SectionDropTarget | null {
  const containerWidth =
    widthOverride && widthOverride > 0 ? widthOverride : grid.containerWidth;
  if (containerWidth <= 0) return null;

  const { config, items } = grid;
  const rowHeight =
    typeof config.rowHeight === "number"
      ? config.rowHeight
      : EDITOR_ROW_HEIGHT_PX;
  const localY = (pointerClientY - originY) / (scale || 1);

  // Section blocks only (don't disturb any non-section top-level item), in
  // visual order, as vertical spans within the grid container.
  const spans: SectionSpan[] = Object.values(items)
    .filter((it) => it.contentType === "grid")
    .sort((a, b) => a.position.y - b.position.y)
    .map((it) => {
      const r = gridToPixel(
        it.position,
        containerWidth,
        config.columns,
        rowHeight,
        gapOf(config),
        config.padding,
      );
      return { top: r.top, height: r.height };
    });

  const index = pickSectionInsertIndex(spans, localY);
  return {
    index,
    markerTop: sectionMarkerTop(spans, index, gapOf(config).y, config.padding),
  };
}

/**
 * Web adapter: resolve where a section dragged to `pointerClientY` would land
 * in `pageGridId`. Measures the PAGE BODY (`[data-page-grid]`) — which wraps the
 * whole page body and is present at EVERY zoom level, unlike the real grid's
 * `[data-grid-container]` which isn't mounted at far zoom (the SVG wireframe is)
 * — then delegates to the pure `computeSectionDropTargetFromGeometry`. The
 * native adapter mirrors this with a measured (measureInWindow) origin.
 *
 * Returns null when the grid/DOM isn't found — callers fall back to appending.
 */
export function computeSectionDropTarget(
  pageGridId: string,
  pointerClientY: number,
): SectionDropTarget | null {
  const el = document.querySelector(
    `[data-page-grid="${escapeAttr(pageGridId)}"]`,
  ) as HTMLElement | null;
  if (!el) return null;

  const grid = useGridStore.getState().grids[pageGridId];
  if (!grid) return null;

  // The page body is xyflow-scaled by the canvas zoom; derive that scale from
  // its rendered vs layout width so the screen pointer maps to grid-local px.
  // Its layout width IS the grid's content width, so it also stands in for a
  // stale/zero stored containerWidth at far zoom. STAYS post grid-0.9.4: it
  // measures the `[data-page-grid]` wrapper (present at every zoom level,
  // unlike the grid container) — no scale-aware ResolvedGrid is involved.
  const rect = el.getBoundingClientRect();
  const scale = el.offsetWidth > 0 ? rect.width / el.offsetWidth : 1;

  return computeSectionDropTargetFromGeometry(
    grid,
    rect.top,
    pointerClientY,
    scale,
    el.offsetWidth,
  );
}
